import React, { useState, useEffect } from 'react';
import Select from './Select';

const SCHEDULE_OPTIONS = [
  { value: 'monday', label: 'ПН' },
  { value: 'tuesday', label: 'ВТ' },
  { value: 'wednesday', label: 'СР' },
  { value: 'thursday', label: 'ЧТ' },
  { value: 'friday', label: 'ПТ' },
  { value: 'saturday', label: 'СБ' },
  { value: 'sunday', label: 'ВС' }
];

const QUICK_SCHEDULES = [
  { value: 'custom', label: 'Свой' },
  { value: '5_2', label: '5/2' },
  { value: '6_1', label: '6/1' },
  { value: 'shift', label: 'Вахта' }
];

const SHIFT_TEMPLATES = [
  { value: '15_15', label: '15/15' },
  { value: '30_30', label: '30/30' },
  { value: 'custom', label: 'Другое' }
];

const STANDARD_HOURS_OPTIONS = [
  { value: '8', label: '8 часов' },
  { value: '10', label: '10 часов' },
  { value: '12', label: '12 часов' },
  { value: 'custom', label: 'Другое' }
];

const TEACHER_HOURS_OPTIONS = [
  { value: 'up_to_3', label: 'До 3-х часов' },
  { value: 'more_than_3', label: 'От 3-х часов и более' },
  { value: 'custom', label: 'Другое' }
];

const ScheduleInput = ({ value, onChange, category }) => {
  const [error, setError] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  const currentScheduleType = value?.scheduleType || 'custom';

  // Выбираем нужные опции часов в зависимости от категории
  const hoursOptions = category === 'teacher-search' ? TEACHER_HOURS_OPTIONS : STANDARD_HOURS_OPTIONS;

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 300);
    return () => clearTimeout(timer);
  }, [currentScheduleType]);

  const handleDayToggle = (dayValue) => {
    const currentDays = value?.days || [];
    let newDays;
    
    if (currentDays.includes(dayValue)) {
      newDays = currentDays.filter(day => day !== dayValue);
    } else {
      newDays = [...currentDays, dayValue];
    }

    setError(newDays.length === 0 ? 'Выберите хотя бы один день' : '');

    onChange({
      target: {
        name: 'schedule',
        value: {
          ...value,
          scheduleType: 'custom',
          days: newDays
        }
      }
    });
  };

  const handleQuickSchedule = (scheduleType) => {
    setError('');
    
    onChange({
      target: {
        name: 'schedule',
        value: {
          scheduleType,
          days: [],
          hours: value?.hours || '',
          shiftDetails: scheduleType === 'shift' ? value?.shiftDetails || '' : ''
        }
      }
    });
  };

  const handleHoursChange = (option) => {
    if (option === 'custom') {
      onChange({
        target: {
          name: 'schedule',
          value: {
            ...value,
            hours: ''
          }
        }
      });
    } else {
      onChange({
        target: {
          name: 'schedule',
          value: {
            ...value,
            hours: option
          }
        }
      });
    }
  };

  const handleCustomHoursChange = (e) => {
    const hours = e.target.value.replace(/[^0-9]/g, '');
    if (hours === '') {
      setError('');
      handleHoursChange('');
    } else {
      const numHours = parseInt(hours);
      const maxHours = category === 'teacher-search' ? 8 : 24;
      if (numHours > 0 && numHours <= maxHours) {
        setError('');
        handleHoursChange(hours);
      } else {
        setError(`Введите число от 1 до ${maxHours}`);
      }
    }
  };

  const handleShiftDetailsChange = (e) => {
    const details = e.target.value;
    if (!details.match(/^\d{1,2}\/\d{1,2}$/) && details !== '') {
      setError('Формат: число/число (например: 15/15)');
    } else {
      setError('');
    }

    onChange({
      target: {
        name: 'schedule',
        value: {
          ...value,
          shiftDetails: details
        }
      }
    });
  };

  return (
    <div className={`schedule-input ${isAnimating ? 'animating' : ''}`}>
      <div className="schedule-selector">
        {QUICK_SCHEDULES.map(schedule => (
          <button
            key={schedule.value}
            type="button"
            className={`schedule-selector-button ${currentScheduleType === schedule.value ? 'active' : ''}`}
            onClick={() => handleQuickSchedule(schedule.value)}
          >
            {schedule.label}
          </button>
        ))}
      </div>

      {currentScheduleType === 'custom' && (
        <div className="custom-schedule fade-in">
          <div className="days-selector">
            {SCHEDULE_OPTIONS.map(day => (
              <button
                key={day.value}
                type="button"
                className={`day-selector-button ${value?.days?.includes(day.value) ? 'active' : ''}`}
                onClick={() => handleDayToggle(day.value)}
              >
                {day.label}
              </button>
            ))}
          </div>
          <div className="schedule-hint">Нажмите на дни для выбора</div>
        </div>
      )}

      {currentScheduleType === 'shift' && (
        <div className="shift-details fade-in">
          <textarea
            placeholder="Опишите график вахты (например: 15/15)"
            value={value?.shiftDetails || ''}
            onChange={handleShiftDetailsChange}
            className="shift-input"
          />
        </div>
      )}

      {currentScheduleType !== 'shift' && (
        <div className="hours-input fade-in">
          <label className="schedule-sublabel">
            {category === 'teacher-search' ? 'Длительность занятия:' : 'Количество часов в день:'}
            <div className="schedule-hint">
              {category === 'teacher-search' 
                ? 'Выберите длительность занятия'
                : 'Выберите или введите количество рабочих часов'}
            </div>
          </label>
          <div className="hours-selector">
            {hoursOptions.map(option => (
              <button
                key={option.value}
                type="button"
                className={`hours-selector-button ${
                  option.value === 'custom' 
                    ? (value?.hours && !hoursOptions.map(opt => opt.value).includes(value.hours))
                    : value?.hours === option.value
                  ? 'active' 
                  : ''
                }`}
                onClick={() => handleHoursChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {(value?.hours === 'custom' || (!hoursOptions.map(opt => opt.value).includes(value?.hours) && value?.hours !== undefined)) && (
            <input
              type="number"
              value={value?.hours || ''}
              onChange={handleCustomHoursChange}
              placeholder={category === 'teacher-search' ? 'Введите количество часов' : 'Введите количество часов'}
              className="custom-hours-input"
              min="1"
              max={category === 'teacher-search' ? '8' : '24'}
              pattern="\d*"
              inputMode="numeric"
            />
          )}
        </div>
      )}

      {error && <div className="schedule-error">{error}</div>}
    </div>
  );
};

export default ScheduleInput;